/**
 * Create Workspace flow — name → slug → owner (#499).
 *
 * POST /auth/v1/tenants makes the caller owner and flips their
 * active-tenant preference server-side; the optional invite step uses
 * the invites API (membership-checked, so it works with the pre-switch
 * token). Finishing force-refreshes the session so the new tenant's
 * claims are minted, then lands on the new subdomain when slugged.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Check,
  Plus,
  Rocket,
  Users,
  X,
} from 'lucide-react';
import { Spinner } from '@systemdox/ui';
import { getAuthAdapter } from '@puglieseweb/shared-react-auth';
import { errorStatus, problemDetail, tenantApi } from '@/services/tenantApi';
import { isValidSlug, suggestSlug, tenantUrl, tenantHost, tenantHostSuffix } from '@/utils/subdomain';
import { cn } from '@/lib/utils';

type SlugState = 'idle' | 'invalid' | 'checking' | 'available' | 'taken';
type InviteRole = 'admin' | 'editor' | 'viewer';

interface InviteRow {
  email: string;
  role: InviteRole;
}

const inputClass =
  'w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:border-purple-500 focus:outline-none disabled:opacity-60';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function CreateWorkspacePage() {
  const navigate = useNavigate();

  // ── Step 1: name + slug ────────────────────────────────────────────
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugState, setSlugState] = useState<SlugState>('idle');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const checkTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Keep the slug suggestion following the name until the user edits it.
  useEffect(() => {
    if (!slugTouched) setSlug(suggestSlug(name));
  }, [name, slugTouched]);

  // Debounced live availability check against the public slug endpoint.
  useEffect(() => {
    clearTimeout(checkTimer.current);
    if (!slug) {
      setSlugState('idle');
      return;
    }
    if (!isValidSlug(slug)) {
      setSlugState('invalid');
      return;
    }
    setSlugState('checking');
    checkTimer.current = setTimeout(async () => {
      try {
        const { available } = await tenantApi.checkSlug(slug);
        setSlugState(available ? 'available' : 'taken');
      } catch {
        // Availability is advisory — the create call is the authority.
        setSlugState('idle');
      }
    }, 400);
    return () => clearTimeout(checkTimer.current);
  }, [slug]);

  // ── Step 2: created + optional invites ─────────────────────────────
  const [created, setCreated] = useState<{ tenantId: string; slug?: string } | null>(null);
  const [invites, setInvites] = useState<InviteRow[]>([{ email: '', role: 'editor' }]);
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [finishing, setFinishing] = useState(false);

  const canCreate =
    name.trim().length > 0 &&
    !creating &&
    (slug === '' || slugState === 'available' || slugState === 'checking' || slugState === 'idle');

  const create = async () => {
    if (!canCreate) return;
    setCreating(true);
    setCreateError(null);
    try {
      const result = await tenantApi.createTenant({
        displayName: name.trim(),
        ...(slug ? { slug } : {}),
      });
      setCreated({ tenantId: result.tenantId, slug: result.slug });
    } catch (err) {
      setCreateError(
        errorStatus(err) === 409
          ? `The URL "${tenantHost(slug)}" is already taken — pick another.`
          : problemDetail(err, 'Could not create the workspace.'),
      );
    } finally {
      setCreating(false);
    }
  };

  const finish = async (tenant: { tenantId: string; slug?: string }) => {
    setFinishing(true);
    try {
      // The server already flipped the active-tenant preference; the
      // forced refresh mints the new workspace's claims.
      await getAuthAdapter().fetchSession({ forceRefresh: true });
    } catch {
      // Non-fatal: the reload below re-authenticates from cookies.
    }
    window.location.assign(tenant.slug ? tenantUrl(tenant.slug) : '/');
  };

  const sendInvitesAndFinish = async () => {
    if (!created || inviting) return;
    const rows = invites
      .map((r) => ({ ...r, email: r.email.trim().toLowerCase() }))
      .filter((r) => r.email);
    if (rows.length === 0) {
      finish(created);
      return;
    }
    const bad = rows.find((r) => !EMAIL_PATTERN.test(r.email));
    if (bad) {
      setInviteError(`"${bad.email}" doesn't look like an email address.`);
      return;
    }
    setInviting(true);
    setInviteError(null);
    try {
      await tenantApi.createInvites(created.tenantId, rows);
      finish(created);
    } catch (err) {
      setInviting(false);
      setInviteError(problemDetail(err, 'Could not send the invites.'));
    }
  };

  const slugFeedback: Record<SlugState, { text: string; tone: string } | null> = {
    idle: null,
    checking: { text: 'Checking availability…', tone: 'text-slate-400' },
    available: { text: 'Available', tone: 'text-emerald-600 dark:text-emerald-400' },
    taken: { text: 'Already taken', tone: 'text-red-600 dark:text-red-400' },
    invalid: {
      text: '3-63 characters: lowercase letters, numbers and hyphens.',
      tone: 'text-red-600 dark:text-red-400',
    },
  };

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          Create a workspace
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          A shared space for your team's docs, specs and standards. You'll be the owner.
        </p>
      </div>

      {!created ? (
        <div className="space-y-5 rounded-lg border border-slate-200 dark:border-slate-800 p-6">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Workspace name
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              autoFocus
              placeholder="Acme Engineering"
              className={inputClass}
              disabled={creating}
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Workspace URL <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <div className="flex items-center rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus-within:border-purple-500">
              <input
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value.toLowerCase());
                }}
                placeholder="acme"
                className="w-full bg-transparent px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none disabled:opacity-60"
                disabled={creating}
              />
              <span className="flex items-center gap-1.5 pr-3 text-sm text-slate-400">
                {tenantHostSuffix()}
                {slugState === 'checking' && <Spinner size="sm" aria-label="Checking availability" />}
                {slugState === 'available' && <Check className="w-3.5 h-3.5 text-emerald-500" />}
                {(slugState === 'taken' || slugState === 'invalid') && (
                  <X className="w-3.5 h-3.5 text-red-500" />
                )}
              </span>
            </div>
            {slugFeedback[slugState] && (
              <p className={cn('mt-1.5 text-xs', slugFeedback[slugState]!.tone)}>
                {slugFeedback[slugState]!.text}
              </p>
            )}
          </div>

          {createError && (
            <p className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
              <AlertCircle className="w-4 h-4 shrink-0" /> {createError}
            </p>
          )}

          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              onClick={create}
              disabled={!canCreate || slugState === 'taken' || slugState === 'invalid'}
              className="flex items-center gap-2 rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating ? <Spinner size="sm" tone="current" /> : <Rocket className="w-4 h-4" />}
              Create workspace
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-5 rounded-lg border border-slate-200 dark:border-slate-800 p-6">
          <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
            <Check className="w-5 h-5" />
            <span className="text-sm font-medium">
              {name.trim()} is ready{created.slug ? ` at ${tenantHost(created.slug)}` : ''}.
            </span>
          </div>

          <div>
            <div className="mb-1.5 flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Invite your team <span className="font-normal text-slate-400">(optional)</span>
              </span>
            </div>
            <div className="space-y-2">
              {invites.map((row, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={row.email}
                    onChange={(e) => {
                      const next = [...invites];
                      next[i] = { ...row, email: e.target.value };
                      setInvites(next);
                      setInviteError(null);
                    }}
                    placeholder="teammate@example.com"
                    type="email"
                    className={inputClass}
                    disabled={inviting || finishing}
                  />
                  <select
                    value={row.role}
                    onChange={(e) => {
                      const next = [...invites];
                      next[i] = { ...row, role: e.target.value as InviteRole };
                      setInvites(next);
                    }}
                    disabled={inviting || finishing}
                    className="rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-2 text-sm text-slate-700 dark:text-slate-300 focus:border-purple-500 focus:outline-none"
                  >
                    <option value="admin">Admin</option>
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  {invites.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setInvites(invites.filter((_, j) => j !== i))}
                      className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                      title="Remove"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {invites.length < 10 && (
              <button
                type="button"
                onClick={() => setInvites([...invites, { email: '', role: 'editor' }])}
                disabled={inviting || finishing}
                className="mt-2 flex items-center gap-1 text-sm text-purple-600 dark:text-purple-400 hover:underline"
              >
                <Plus className="w-3.5 h-3.5" /> Add another
              </button>
            )}
          </div>

          {inviteError && (
            <p className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
              <AlertCircle className="w-4 h-4 shrink-0" /> {inviteError}
            </p>
          )}

          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              onClick={sendInvitesAndFinish}
              disabled={inviting || finishing}
              className="flex items-center gap-2 rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
            >
              {inviting || finishing ? <Spinner size="sm" tone="current" /> : <Rocket className="w-4 h-4" />}
              {invites.some((r) => r.email.trim()) ? 'Send invites & open workspace' : 'Open workspace'}
            </button>
            <button
              type="button"
              onClick={() => created && finish(created)}
              disabled={inviting || finishing}
              className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            >
              Skip for now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
